import React, { useState, useEffect } from 'react';
import { colors } from '../../constants/theme';
import { rankingsService } from '../../services/api';
import ConfrontationCard from './ConfrontationCard';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';

const EliminationBracket = ({ phase, category }) => {
  const [bracketData, setBracketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadBracket();
  }, [phase, category]);

  const loadBracket = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await rankingsService.getBracket(phase);
      
      if (response.success) {
        setBracketData(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar chaveamento:', error);
      setError('Erro ao carregar chaveamento');
    } finally {
      setLoading(false);
    }
  };

  const renderConfrontation = (match, matchKey, title, winnerLabel = 'Vencedor') => (
    <ConfrontationCard
      key={matchKey}
      match={match}
      title={title}
      winnerLabel={winnerLabel}
    />
  );

  if (loading) {
    return <LoadingSpinner size="medium" text="Carregando chaveamento..." />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={loadBracket} />;
  }

  if (!bracketData || !bracketData.matches) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-gray-500">Nenhum dado de chaveamento disponível</p>
      </div>
    );
  }

  const getTitle = () => {
    switch (phase) {
      case 'quartas':
        return `QUARTAS DE FINAL - CATEGORIA ${category.toUpperCase()}`;
      case 'semifinal':
        return `SEMIFINAL - CATEGORIA ${category.toUpperCase()}`;
      case 'final':
        return hasThirdPlace
          ? `FINAL E DISPUTA DE TERCEIRO - CATEGORIA ${category.toUpperCase()}`
          : `FINAL - CATEGORIA ${category.toUpperCase()}`;
      default:
        return 'ELIMINATÓRIAS';
    }
  };

  const getMatches = () => {
    // ✅ CORREÇÃO: Sempre usar categoria para todas as fases
    return bracketData.matches[category] || [];
  };

  const matches = getMatches();
  // Na aba da final vêm duas partidas: a final e a disputa de 3º lugar.
  const hasThirdPlace = matches.some((m) => m.phase === 'terceiro');

  if (matches.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="text-white p-4 text-center font-bold" style={{ backgroundColor: colors.primary }}>
          {getTitle()}
        </div>
        <div className="p-8 text-center">
          <p className="text-gray-500">Nenhuma partida encontrada para esta categoria/fase</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="text-white p-4 text-center font-bold" style={{ backgroundColor: colors.primary }}>
        {getTitle()}
      </div>
      <div className="p-6">
        {phase === 'final' && (
          <div className="mb-6 text-center p-4 bg-gradient-to-r from-yellow-100 to-yellow-200 rounded-lg">
            <h3 className="font-bold text-2xl text-yellow-800 mb-2">🏆 DECISÃO DA CATEGORIA {category.toUpperCase()} 🏆</h3>
            <p className="text-yellow-700">
              {hasThirdPlace
                ? 'Os confrontos que definem o campeão e o terceiro colocado!'
                : 'O confronto que definirá o campeão da categoria!'}
            </p>
          </div>
        )}

        <div className="space-y-6">
          {matches.map((match, index) => {
            if (match.phase === 'terceiro') {
              return renderConfrontation(
                match,
                match.id || index,
                '🥉 DISPUTA DE TERCEIRO',
                '3º lugar',
              );
            }

            const title =
              match.phase === 'final' || phase === 'final'
                ? 'FINAL'
                : phase === 'semifinal'
                ? `Semifinal ${index + 1}`
                : `Confronto ${index + 1}`;

            return renderConfrontation(
              match,
              match.id || index,
              title,
              match.phase === 'final' ? 'Campeão' : 'Vencedor',
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EliminationBracket;